<template>
  <Card class="map-card">
    <div class="text-xs-center pt-3 pb-3">STATE OF CHARGE</div>
    <v-divider class="custom-divider" />

    <v-carousel v-if="batteryCount > 2" class="transparent-carousel" hide-delimiters height="460px" :cycle="true">
      <v-carousel-item v-for="(item, id) in mainBatteryList" :key="id">
        <div class="d-flex flex-column fill-height">
          <div class="battery-inner-card">
            <div class="battery-name mb-2 text-xs-center">{{ item.name || `Battery ${id}` }}</div>
            <v-divider class="mb-3 custom-divider" />
            
            <div class="d-flex flex-column align-center justify-center" style="flex: 1;">
              <div class="battery-container mb-3">
                <div class="battery-cap"></div>
                <div class="battery-body">
                  <div class="battery-fill" :style="batteryStyle(item.soc)">
                    <svg class="wave-svg" :style="item.soc >= 100 ? 'animation: none' : ''" viewBox="0 0 200 20"
                      preserveAspectRatio="none">
                      <path d="M0 0 H200 V10 Q175 14 150 10 T100 10 T50 10 T0 10 Z" fill="#2c3e50" />
                    </svg>
                  </div>
                </div>
              </div>

              <v-layout row wrap justify-center class="width-100 px-4">
                <v-flex xs6 class="mb-2 text-xs-center">
                  <div class="stat-label">Charge</div>
                  <span class="stat-value" :class="item.soc == null ? 'error--text' : 'number--text'">
                    {{ item.soc == null ? "N/A" : item.soc }}<span class="unit">%</span>
                  </span>
                </v-flex>
                <v-flex xs6 class="mb-2 text-xs-center">
                  <div class="stat-label">Max Cell V</div>
                  <span :class="item.max_cell_vlt == null ? 'na-value' : 'value'" class="stat-value">
                    {{ item.max_cell_vlt == null ? 'N/A' : item.max_cell_vlt }}<span class="unit">V</span>
                  </span>
                </v-flex>
                <v-flex xs6 class="mb-2 text-xs-center">
                  <div class="stat-label">Health</div>
                  <span :class="item.soh == null ? 'na-value' : 'value'" class="stat-value">
                    {{ item.soh != null ? item.soh : 'N/A' }}<span class="unit">%</span>
                  </span>
                </v-flex>
                <v-flex xs6 class="mb-2 text-xs-center">
                  <div class="stat-label">Max Power</div>
                  <span :class="item.soh == null ? 'na-value' : 'value'" class="stat-value">
                    {{ item.min_cell_vlt == null ? 'N/A' : item.min_cell_vlt }}<span class="unit">V</span>
                  </span>
                </v-flex>
                <v-flex xs12 class="mt-1 text-xs-center">
                  <div class="stat-label mb-1">State</div>
                  <span class="state-pill" :class="(!item.chrg_dischrg_curnt) ? 'na-state' : 'active-state'">
                    {{ getStatusText(item) }}
                  </span>
                </v-flex>
              </v-layout>
            </div>
          </div>
        </div>
      </v-carousel-item>
    </v-carousel>

    <div v-else class="split-view-container">
      <div v-for="(item, id) in mainBatteryList" :key="id" class="split-item"
        :class="batteryCount === 2 ? 'compact-item' : 'full-item'">
        <div class="battery-inner-card text-xs-center px-0">
          <div class="battery-name mb-2" :class="{ 'small-name': batteryCount === 2 }">{{ item.name || `Battery ${id}` }}
          </div>
          <v-divider class="mb-3 custom-divider" />
          
          <div class="d-flex flex-column align-center justify-center width-100" style="flex: 1;">
            <div v-if="batteryCount === 1" class="d-flex flex-column align-center width-100 pa-4">
              <div class="battery-container mb-4">
                <div class="battery-cap"></div>
                <div class="battery-body">
                  <div class="battery-fill" :style="batteryStyle(item.soc)">
                    <svg class="wave-svg" :style="item.soc >= 100 ? 'animation: none' : ''" viewBox="0 0 200 20"
                      preserveAspectRatio="none">
                      <path d="M0 0 H200 V10 Q175 14 150 10 T100 10 T50 10 T0 10 Z" fill="#2c3e50" />
                    </svg>
                  </div>
                </div>
              </div>

              <v-layout row wrap justify-center class="width-100 px-3">
                <v-flex xs6 class="mb-3 text-xs-center">
                  <div class="stat-label">Charge</div>
                  <span class="stat-value" :class="item.soc == null ? 'error--text' : 'number--text'">
                    {{ item.soc == null ? "N/A" : item.soc }}<span class="unit">%</span>
                  </span>
                </v-flex>
                <v-flex xs6 class="mb-3 text-xs-center">
                  <div class="stat-label">Max Cell V</div>
                  <span :class="item.max_cell_vlt == null ? 'na-value' : 'value'" class="stat-value">
                    {{ item.max_cell_vlt == null ? 'N/A' : item.max_cell_vlt }}<span class="unit">V</span>
                  </span>
                </v-flex>
                <v-flex xs6 class="mb-3 text-xs-center">
                  <div class="stat-label">Health</div>
                  <span :class="item.soh == null ? 'na-value' : 'value'" class="stat-value">
                    {{ item.soh != null ? item.soh : 'N/A' }}<span class="unit">%</span>
                  </span>
                </v-flex>
                <v-flex xs6 class="mb-3 text-xs-center">
                  <div class="stat-label">Max Power</div>
                  <span :class="item.min_cell_vlt == null ? 'na-value' : 'value'" class="stat-value">
                    {{ item.min_cell_vlt == null ? 'N/A' : item.min_cell_vlt }}<span class="unit">V</span>
                  </span>
                </v-flex>
                <v-flex xs12 class="mt-1 text-xs-center">
                  <div class="stat-label mb-1">State</div>
                  <span class="state-pill" :class="(!item.chrg_dischrg_curnt) ? 'na-state' : 'active-state'">
                    {{ getStatusText(item) }}
                  </span>
                </v-flex>
              </v-layout>
            </div>

            <div v-else class="d-flex flex-column align-center justify-center width-100 pa-4">
              <div class="battery-container compact-battery mb-3">
                <div class="battery-cap"></div>
                <div class="battery-body">
                  <div class="battery-fill" :style="batteryStyle(item.soc)">
                    <svg class="wave-svg" :style="item.soc >= 100 ? 'animation: none' : ''" viewBox="0 0 200 20"
                      preserveAspectRatio="none">
                      <path d="M0 0 H200 V10 Q175 14 150 10 T100 10 T50 10 T0 10 Z" fill="#2c3e50" />
                    </svg>
                  </div>
                </div>
              </div>

              <div class="text-xs-center" style="width: 100%">
                <v-layout row wrap justify-center>
                  <v-flex xs5 class="mb-2 mt-0 ml-0 mr-0 pa-0 text-xs-center">
                    <div class="stat-label">Charge</div>
                    <span class="stat-value" :class="item.soc == null ? 'error--text' : 'number--text'">
                      {{ item.soc == null ? "N/A" : item.soc }}<span class="unit">%</span>
                    </span>
                  </v-flex>
                  <v-flex xs5 class="mb-2 mt-0 ml-0 mr-0 pa-0 text-xs-center">
                    <div class="stat-label">Max Cell V</div>
                    <span :class="item.max_cell_vlt == null ? 'na-value' : 'value'" class="stat-value">
                      {{ item.max_cell_vlt == null ? 'N/A' : item.max_cell_vlt }}<span class="unit">V</span>
                    </span>
                  </v-flex>
                  <v-flex xs5 class="mb-2 mt-0 ml-0 mr-0 pa-0 text-xs-center">
                    <div class="stat-label">Health</div>
                    <span :class="item.soh == null ? 'na-value' : 'value'" class="stat-value">
                      {{ item.soh != null ? item.soh : 'N/A' }}<span class="unit">%</span>
                    </span>
                  </v-flex>
                  <v-flex xs5 class="mb-2 mt-0 ml-0 mr-0 pa-0 text-xs-center">
                    <div class="stat-label">Max Power</div>
                    <span :class="item.min_cell_vlt == null ? 'na-value' : 'value'" class="stat-value">
                      {{ item.min_cell_vlt == null ? 'N/A' : item.min_cell_vlt }}<span class="unit">V</span>
                    </span>
                  </v-flex>
                  <v-flex xs12 class="mt-1 text-xs-center">
                    <div class="stat-label mb-1">State</div>
                    <span class="state-pill" :class="(!item.chrg_dischrg_curnt) ? 'na-state' : 'active-state'">
                      {{ getStatusText(item) }}
                    </span>
                  </v-flex>
                </v-layout>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Card>
</template>

<script>
export default {
  name: "SoCCard",
  props: {
    mainBatteryList: Object,
  },
  computed: {
    batteryCount() {
      return this.mainBatteryList ? Object.keys(this.mainBatteryList).length : 0;
    }
  },
  methods: {
    getStatusText(item) {
      if (item.chrg_dischrg_curnt == 1) return "Charging";
      if (item.chrg_dischrg_curnt == 0) return "Discharging";
      return "N/A";
    },
    batteryStyle(soc) {
      const percentage = soc || 0;
      let color = '#ff5252'; // Red for low battery
      let shadowColor = 'rgba(255, 82, 82, 0.4)';

      if (percentage >= 20 && percentage < 50) {
        color = '#ff9800'; // Orange for medium
        shadowColor = 'rgba(255, 152, 0, 0.4)';
      } else if (percentage >= 50) {
        color = '#4caf50'; // Green for good
        shadowColor = 'rgba(76, 175, 80, 0.4)';
      }

      return {
        height: `${percentage}%`,
        '--fill-color': color
      };
    },
    lightenDarkenColor(col, amt) {
      let usePound = false;
      if (col[0] == "#") {
        col = col.slice(1);
        usePound = true;
      }
      let num = parseInt(col, 16);
      let r = (num >> 16) + amt;
      if (r > 255) r = 255;
      else if (r < 0) r = 0;
      let b = ((num >> 8) & 0x00FF) + amt;
      if (b > 255) b = 255;
      else if (b < 0) b = 0;
      let g = (num & 0x0000FF) + amt;
      if (g > 255) g = 255;
      else if (g < 0) g = 0;
      return (usePound ? "#" : "") + (g | (b << 8) | (r << 16)).toString(16);
    },
  },
};
</script>

<style scoped>
.map-card {
  height: 400px;
  border: 1px solid #334079 !important;
  border-radius: 8px;
  box-shadow: 0px 5px 5px 0px #334079;
}

.transparent-carousel {
  border: none;
  box-shadow: none;
}

.battery-content {
  min-height: 130px;
}

.battery-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.5));
}

.battery-cap {
  width: 40px;
  height: 12px;
  background-color: var(--v-primaryText-base);
  border-radius: 4px 4px 0 0;
  margin-bottom: 2px;
  background: linear-gradient(to bottom, var(--v-primaryText-base), var(--v-secondaryText-base));
  box-shadow: inset 0 -2px 4px rgba(0, 0, 0, 0.2);
}

.battery-body {
  position: relative;
  width: 100px;
  height: 200px;
  background-color: #2c3e50;
  border: 4px solid var(--v-primaryText-base);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: inset 0 0 10px rgba(0, 0, 0, 0.8);
  background-image: linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0) 100%);
  z-index: 1;
}

.battery-fill {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  border-radius: 0 0 8px 8px;
  transition: height 1s cubic-bezier(0.4, 0, 0.2, 1);
  background-color: var(--fill-color);
  overflow: visible;
  z-index: 2;
}


.wave-svg {
  position: absolute;
  top: -1px;
  left: 0;
  width: 200%;
  height: 20px;
  fill: #2c3e50;
  animation: wave-flow 6s linear infinite;
}

@keyframes wave-flow {
  0% {
    transform: translateX(0);
  }

  100% {
    transform: translateX(-50%);
  }
}

.value,
.text-value,
.na-value {
  font-size: 20px;
  font-weight: 700;
  line-height: 1.2;
}

.value {
  color: #668cf6;
}

.text-value {
  color: #4caf50;
}

.na-value {
  color: #ff5252;
}

.custom-divider {
  border-color: #334079 !important;
}

@media (max-width: 600px) {
  .battery-body {
    height: 150px;
    width: 80px;
  }
}

.battery-name {
  font-size: 16px;
  letter-spacing: 1px;
  padding: 4px 12px;
}

.theme--light .battery-name {
  color: var(--v-black-base);
}

.theme--dark .battery-name {
  color: var(--v-white-base);
}

.stat-label {
  font-size: 0.85rem;
  color: #aab2cd;
  letter-spacing: 0.5px;
}

.stat-value {
  font-size: 18px;
  font-weight: 700;
}

.unit {
  font-size: 12px;
  margin-left: 2px;
  opacity: 0.7;
}

/* Pills */
.state-pill {
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
}

.active-state {
  color: #4caf50;
  border: 1px solid #4caf50;
}

.na-state {
  color: #ff5252;
  border: 1px solid #ff5252;
}

/* Small Compact View */
.stat-label-small {
  font-size: 0.85rem;
  color: #8892b0;
}

.stat-value-small {
  font-size: 16px;
  font-weight: 700;
}

.state-pill-small {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.compact-battery .battery-body {
  width: 60px;
  height: 120px;
  border-width: 3px;
  border-radius: 8px;
}

.compact-battery .battery-cap {
  width: 24px;
  height: 7px;
  border-radius: 2px 2px 0 0;
}

.compact-battery .wave-svg {
  height: 12px;
}

.vertical-divider {
  position: absolute;
  left: 50%;
  top: 10%;
  height: 80%;
  width: 1px;
  background: linear-gradient(to bottom, transparent, #334079, transparent);
  transform: translateX(-50%);
  z-index: 10;
}

.split-view-container {
  display: flex;
  flex-direction: row;
  align-items: stretch;
  justify-content: space-around;
  position: relative;
  width: 100%;
  height: 85%;
  padding: 16px;
}

.split-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 2;
  height: 100%;
}

.full-item {
  width: 100%;
}

.compact-item {
  flex: 1;
  max-width: 50%;
  padding: 0 8px;
}

.small-name {
  font-size: 13px !important;
  padding: 4px 8px !important;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.battery-inner-card {
  border: 1px solid #334079;
  border-radius: 8px;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 8px;
}
</style>
